import PdfCa from '../pdf_ca';
import React, { useMemo } from 'react';
import classNames from 'classnames';
import CaText from '../ca_text';
import { Form } from 'react-bootstrap-5';
import PropTypes from 'prop-types';
import { post } from 'helpers/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDownload, faPencil } from '@fortawesome/pro-solid-svg-icons';

export default function CaContainer({ caData, caFields, setCaFields, trackViewedCaPath }) {
  const tab = useMemo(() => {
    if (caFields.caType == 'digital_signature') return 'text';
    return 'pdf';
  }, [caFields.caType]);

  React.useEffect(() => {
    const pathName = window.location.pathname;
    const hasViewed = sessionStorage.getItem(`viewed_ca_${pathName}`);

    if (!hasViewed) {
      post(trackViewedCaPath).then(() => {
        sessionStorage.setItem(`viewed_ca_${pathName}`, true);
      });
    }
  }, []);

  function changeCaChecked(newValue) {
    setCaFields({ ...caFields, checked: newValue });
  }

  function fileUploaded(uploadedFile) {
    setCaFields({ ...caFields, uploadedFile: uploadedFile });
  }

  const showDigitalSign = useMemo(() => {
    return caData.caTypeStandard || caData.caTypeCustomText;
  }, [caData]);

  const showDownloadAndSign = useMemo(() => {
    return caData.caTypeCustomPdf || caData.caTypeCustomWord;
  }, [caData]);

  const showTabs = useMemo(() => {
    return showDigitalSign && showDownloadAndSign;
  }, [showDigitalSign, showDownloadAndSign]);

  React.useEffect(() => {
    if (showTabs || showDownloadAndSign) {
      setCaFields({ caType: 'uploaded_signature' });
    } else if (showDigitalSign) {
      setCaFields({ caType: 'digital_signature' });
    }
  }, [showDigitalSign, showDownloadAndSign, showTabs]);

  return (
    <div>
      {showTabs && (
        <ul className="nav nav-pills mb-4 justify-content-center">
          {(caData.caTypeStandard || caData.caTypeCustomText) && (
            <li
              className={classNames('nav-link', 'clickable', { active: tab === 'text' })}
              onClick={() => setCaFields({ caType: 'digital_signature' })}
            >
              <FontAwesomeIcon className="me-2" icon={faPencil}/>
              Quick Sign
            </li>
          )}
          {(caData.caTypeCustomPdf || caData.caTypeCustomWord) && (
            <li
              className={classNames('nav-link', 'clickable', { active: tab === 'pdf' })}
              onClick={() => setCaFields({ caType: 'uploaded_signature' })}
            >
              <FontAwesomeIcon className="me-2" icon={faFileDownload}/>
              Download & Sign
            </li>
          )}
        </ul>
      )}
      {(caData.caTypeCustomPdf || caData.caTypeCustomWord) && tab == 'pdf' && (
        <PdfCa
          caFiles={[
            (caData.caTypeCustomPdf && { type: 'custom_pdf', path: caData.pdfFilePath }),
            (caData.caTypeCustomWord && { type: 'custom_word', path: caData.wordFilePath })
          ].filter(n => n)}
          uploadedFile={caFields.uploadedFile}
          onFileUploaded={fileUploaded}
        />
      )}
      {(caData.caTypeStandard || caData.caTypeCustomText) && tab == 'text' && (
        <TextCa
          caChecked={caFields.checked || false}
          caText={caData.text ?? ""}
          digitalSignFilePath={caData.digitalSignFilePath}
          onCaChecked={changeCaChecked}
        />
      )}
    </div>
  );
}

function TextCa({ caChecked, caText, digitalSignFilePath, onCaChecked }) {
  const pdfPreview = useMemo(() => {
    if (!digitalSignFilePath) return null;

    const url = new URL(digitalSignFilePath);
    url.hash = 'toolbar=0&navpanes=0&scrollbar=0&zoom=FitH';
   
    return <iframe height="500px" src={url.toString()} width="100%"/>;
  }, [digitalSignFilePath]);

  return (
    <React.Fragment>
      {pdfPreview}
      {caText.length > 0 && <CaText caText={caText}/>}
      <Form.Group className="mb-4">
        <Form.Check
          checked={caChecked}
          id="email-ca"
          label="Email me a copy of this CA"
          name="email_ca"
          type="checkbox"
          onChange={e => onCaChecked(e.target.checked)}
        />
      </Form.Group>
    </React.Fragment>
  );
}

CaContainer.propTypes = {
  caData: PropTypes.shape({
    digitalSignFilePath: PropTypes.string,
    text: PropTypes.string,
    type: PropTypes.string,
    filePath: PropTypes.string,
  }).isRequired,
  caFields: PropTypes.object.isRequired,
  setCaFields: PropTypes.func.isRequired,
  trackViewedCaPath: PropTypes.string.isRequired,
};

TextCa.propTypes = {
  caChecked: PropTypes.bool.isRequired,
  caText: PropTypes.string.isRequired,
  digitalSignFilePath: PropTypes.string,
  onCaChecked: PropTypes.func.isRequired,
};
